/**
 * OpenAI SDK adapter for edit_file tool
 */

import type { ChatCompletionTool } from 'openai/resources/chat/completions';
import { executeEditFile } from './core.js';
import { EDIT_FILE_TOOL_DESCRIPTION, EDIT_FILE_SYSTEM_PROMPT } from './prompts.js';
import type { EditFileInput, EditFileResult, EditFileConfig } from './types.js';

/**
 * OpenAI-native tool definition for edit_file
 * 
 * @example
 * ```ts
 * import OpenAI from 'openai';
 * import { editFileTool } from 'morphsdk/tools/openai';
 * 
 * const client = new OpenAI({ apiKey: process.env.OPENAI_API_KEY });
 * 
 * const response = await client.chat.completions.create({
 *   model: "gpt-4o",
 *   tools: [editFileTool],
 *   messages: [{ role: "user", content: "Fix the bug in app.ts" }]
 * });
 * ```
 */
export const editFileTool: ChatCompletionTool = {
  type: 'function',
  function: {
    name: 'edit_file',
    description: EDIT_FILE_TOOL_DESCRIPTION,
    parameters: {
      type: 'object',
      properties: {
        target_filepath: {
          type: 'string',
          description: 'The path of the target file to modify',
        },
        instruction: {
          type: 'string',
          description: 'A single sentence written in the first person describing what the agent is changing. Used to help disambiguate uncertainty in the edit.',
        },
        code_edit: {
          type: 'string',
          description: 'Specify ONLY the precise lines of code that you wish to edit. Use `// ... existing code ...` for unchanged sections.',
        },
      },
      required: ['target_filepath', 'instruction', 'code_edit'],
    },
  },
};

/**
 * Execute an edit_file tool call
 * 
 * @param input - The tool input from GPT (parsed from tool_calls)
 * @param config - Optional configuration
 * @returns The result of the edit operation
 * 
 * @example
 * ```ts
 * const args = JSON.parse(toolCall.function.arguments);
 * const result = await execute(args);
 * console.log('Changes applied:', result.udiff);
 * ```
 */
export async function execute(
  input: EditFileInput,
  config?: EditFileConfig
): Promise<EditFileResult> {
  return executeEditFile(input, config);
}

/**
 * Get the system prompt for edit_file usage
 * 
 * Add this to your system message to guide GPT on using edit_file properly.
 * 
 * @example
 * ```ts
 * const response = await client.chat.completions.create({
 *   model: "gpt-4o",
 *   messages: [
 *     { role: "system", content: getSystemPrompt() },
 *     { role: "user", content: "Fix bugs" }
 *   ],
 *   tools: [editFileTool]
 * });
 * ```
 */
export function getSystemPrompt(): string {
  return EDIT_FILE_SYSTEM_PROMPT;
}

/**
 * Format the result for passing back to GPT
 * 
 * @param result - The edit result
 * @returns Formatted string for tool message
 */
export function formatResult(result: EditFileResult): string {
  if (!result.success) {
    return `Error editing file: ${result.error}`;
  }
  
  const { changes } = result;
  return `Successfully applied changes to ${result.filepath}. Added ${changes.linesAdded}, removed ${changes.linesRemoved}, modified ${changes.linesModified}.`;
}

/**
 * Create a custom edit_file tool with configuration and methods
 * 
 * @param config - Configuration options
 * @returns Tool definition with execute and formatResult methods
 * 
 * @example
 * ```ts
 * import OpenAI from 'openai';
 * import { createEditFileTool } from 'morphsdk/tools/fastapply/openai';
 * 
 * const tool = createEditFileTool({
 *   baseDir: './src',
 *   generateUdiff: true,
 *   description: 'Custom tool description for your use case'
 * });
 * 
 * const client = new OpenAI();
 * 
 * const response = await client.chat.completions.create({
 *   model: 'gpt-4o',
 *   tools: [tool], // tool itself is the ChatCompletionTool
 *   messages: [{ role: 'user', content: 'Fix bug in app.ts' }]
 * });
 * 
 * // Execute and format
 * const result = await tool.execute(toolCallArgs);
 * const formatted = tool.formatResult(result);
 * ```
 */
export function createEditFileTool(config: EditFileConfig = {}) {
  const toolDef: ChatCompletionTool = {
    ...editFileTool,
    ...(config.description && {
      function: {
        ...editFileTool.function,
        description: config.description,
      },
    }),
  };
  
  return Object.assign({}, toolDef, {
    execute: async (input: EditFileInput | string): Promise<EditFileResult> => {
      const parsedInput = typeof input === 'string' ? JSON.parse(input) : input;
      return execute(parsedInput, config);
    },
    formatResult: (result: EditFileResult): string => {
      return formatResult(result);
    },
    getSystemPrompt: (): string => {
      return getSystemPrompt();
    },
  });
}

// Default export for convenience
export default editFileTool;

